/**
 * The Library page (`/ui/library`): the workspace's uploaded files as a list
 * or a masonry grid, loaded a page at a time as the reader scrolls.
 */

/**
 * Files per page. A grid of 480px thumbnails five across shows about twenty
 * at once, so a page is two screens: enough that scrolling rarely catches up
 * with the next one.
 */
export const LIBRARY_PAGE_SIZE = 40;

/** How far ahead of the end of the list the next page is asked for. */
export const LIBRARY_PREFETCH_MARGIN = '800px';

/** How many files go up at once when several are picked together. */
export const LIBRARY_UPLOAD_CONCURRENCY = 3;

/** How long an upload waits to read its image's size locally before going without. */
export const LIBRARY_PREVIEW_READ_TIMEOUT_MS = 1500;

/** How long the search box waits after the last keystroke before it asks. */
export const LIBRARY_SEARCH_SETTLE_MS = 250;

/**
 * List rows hold a 36px thumbnail with room around it; the list table's
 * one-line 40px would crop it.
 */
export const LIBRARY_ROW_HEIGHT = 56;

/** A grid column never gets narrower than this; past it the grid drops a column. */
export const LIBRARY_GRID_MIN_COLUMN_WIDTH = 210;

/** No more columns than this, however wide the page. */
export const LIBRARY_GRID_MAX_COLUMNS = 6;

/** Space between grid tiles, across and down. */
export const LIBRARY_GRID_GAP = 16;

/**
 * A tile's height over its width is held between these, so a panorama or a
 * tall screenshot does not turn into a sliver or a tower. The image fills
 * the tile and the excess is cropped.
 */
export const LIBRARY_TILE_MIN_RATIO = 0.5;
export const LIBRARY_TILE_MAX_RATIO = 2;

/**
 * An image whose size was never recorded (uploaded before sizes were kept)
 * gets a square tile, filled and cropped, rather than one that jumps when the
 * image arrives.
 */
export const LIBRARY_TILE_UNKNOWN_RATIO = 1;

/** A PDF's tile: a card with its name on top and its mark below, a little taller than wide. */
export const LIBRARY_DOCUMENT_TILE_RATIO = 1.2;

/** Every library list in the cache, whatever it is narrowed to. */
export const LIBRARY_FILES_QUERY_KEY = [ 'library-files' ] as const;

/** The Publish flow's image picker: the same uploads, images only. */
export const IMAGE_ASSETS_QUERY_KEY = [ 'image-assets' ] as const;

/**
 * Every cached list of uploaded files. An upload from anywhere (a chat, the
 * image picker, the Library itself) makes each of them out of date.
 */
export const UPLOADED_FILE_LIST_QUERY_KEYS = [ LIBRARY_FILES_QUERY_KEY, IMAGE_ASSETS_QUERY_KEY ] as const;

/** Where the view choice is remembered between visits. */
export const LIBRARY_VIEW_STORAGE_KEY = 'strive.library.view';

/** Room around an image in the open file's panel, on every side. */
export const LIBRARY_PANEL_IMAGE_INSET = 24;

/**
 * The shape an image's skeleton takes in the panel when its size was never
 * recorded: a landscape photo's, the commonest upload.
 */
export const LIBRARY_PANEL_UNKNOWN_IMAGE_SIZE = { width: 4, height: 3 } as const;

/**
 * How long a PDF's skeleton waits for the viewer to say it has loaded before
 * stepping aside anyway. A browser that never fires `load` on the frame must
 * not leave the document hidden under a placeholder.
 */
export const LIBRARY_PANEL_DOCUMENT_SKELETON_MAX_MS = 8000;
